import { z } from "zod";
import { DateValidation } from "../shared/date-validation";

/**
 * KB article subject taxonomy node. Served nested inside GET /kb/articles/{number}.
 */
export const KnowledgeBaseArticleSubjectSchema = z.object({
  Id: z.string().nullable().optional(),
  Title: z.string().nullable().optional(),
  Description: z.string().nullable().optional(),
  ParentId: z.string().nullable().optional(),
});

export type KnowledgeBaseArticleSubjectType = z.infer<
  typeof KnowledgeBaseArticleSubjectSchema
>;

/**
 * KB article author. Served nested inside GET /kb/articles/{number}.
 */
export const KnowledgeBaseArticlePrimaryAuthorSchema = z.object({
  Id: z.string().nullable().optional(),
  FirstName: z.string().nullable().optional(),
  LastName: z.string().nullable().optional(),
  MiddleName: z.string().nullable().optional(),
  FullName: z.string().nullable().optional(),
  JobTitle: z.string().nullable().optional(),
});

export type KnowledgeBaseArticlePrimaryAuthorType = z.infer<
  typeof KnowledgeBaseArticlePrimaryAuthorSchema
>;

/**
 * A knowledge base article as returned by GET /kb/articles/{number}.
 * Dataverse returns sparse records, so everything but Id is optional.
 */
export const KnowledgeBaseArticleSchema = z.object({
  Id: z.string().uuid(),
  Title: z.string().nullable().optional(),
  Number: z.string().nullable().optional(),
  Text: z.string().nullable().optional(),
  Created: DateValidation,
  Description: z.string().nullable().optional(),
  Keywords: z.string().nullable().optional(),
  Views: z.number().nullable().optional(),
  ImageCover: z.string().nullable().optional(),
  ImageThumbnail: z.string().nullable().optional(),
  IsMarkdown: z.boolean().nullable().optional(),
  Published: DateValidation,
  Modified: DateValidation,
  WordsCount: z.number().nullable().optional(),
  TimeToReadMinutes: z.number().nullable().optional(),
  Subject: KnowledgeBaseArticleSubjectSchema.nullable().optional(),
  PrimaryAuthor: KnowledgeBaseArticlePrimaryAuthorSchema.nullable().optional(),
});

export type KnowledgeBaseArticleType = z.infer<
  typeof KnowledgeBaseArticleSchema
>;

/**
 * The current user's reaction to an article. GET /kb/articles/{id}/reaction may
 * respond with an empty body when there is no reaction yet — parse that as null.
 */
export const KnowledgeBaseArticleReactionSchema = z
  .union([z.enum(["Like", "Dislike"]), z.literal("")])
  .nullable()
  .optional()
  .transform((value) => (value ? value : null));

export type KnowledgeBaseArticleReactionType = "Like" | "Dislike" | null;
